/**
 * @typedef {import("axios").AxiosRequestConfig & { _isRetryRequest?: boolean }} AxiosRequestConfig
 */

/**
 * @template T
 * @typedef {import("axios").AxiosResponse<T>} AxiosResponse
 */

/**
 * @template T
 * @typedef {import("axios").AxiosError<T>} AxiosError
 */

/**
 * @typedef {import("@/types/api.type").ApiResponse<T>} ApiResponse
 */

import axios from "axios";

import config from "@/common/config";
import { getLocale } from "@/i18n";

/**
 * @param {AxiosResponse<ApiResponse>} response
 *
 * @returns {boolean}
 */
export const isValidationFailedResponse = (response) =>
  response?.status === 422 && response?.data.message === "validation_failed";

/**
 * @param {AxiosResponse<ApiResponse>} response
 * @param {string} param
 *
 * @returns {boolean}
 */
export const isValidationAccessResponse = (response, param) => {
  if (!isValidationFailedResponse(response)) {
    return false;
  }

  const paramErrors = response?.data.data[param] ?? [];
  return paramErrors.includes("validation.access");
};

/**
 * @param {AxiosRequestConfig} config
 */
const requestInterceptor = (config) => {
  config.headers["Accept-Language"] = getLocale();
  return config;
};

/**
 * Axios instance for API
 */
const api = axios.create({
  baseURL: config.api.baseUrl,
  withCredentials: true
});

api.interceptors.request.use(requestInterceptor);

const createApiBase = (namespace, version) => {
  let base = "";
  if (namespace) {
    base += `${namespace}/`;
  }

  return base + version;
};

const ApiService = {
  api,

  /**
   * @param {string} url
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  get(url, version, namespace = "") {
    return api.get(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`);
  },

  /**
   * @param {string} url
   * @param {Object} data
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  post(url, data, version, namespace = "") {
    return api.post(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`, data);
  },

  /**
   * @param {string} url
   * @param {Object} data
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  update(url, data, version, namespace = "") {
    return api.put(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`, data);
  },

  /**
   * @param {string} url
   * @param {Object} data
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  put(url, data, version, namespace = "") {
    return api.put(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`, data);
  },

  /**
   * @param {string} url
   * @param {Object} data
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  patch(url, data, version, namespace = "") {
    return api.patch(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`, data);
  },

  /**
   * @param {string} url
   * @param {string} version
   * @param {string} namespace
   *
   * @returns {Promise<AxiosResponse<ApiResponse>>}
   */
  delete(url, version, namespace = "") {
    return api.delete(`/${createApiBase(namespace, version)}/${url.replace(/^\//, "")}`);
  }
};

export default ApiService;
